<template>
  <!-- Batch Progress Modal -->
  <div class="modal fade batch-progress-modal" tabindex="-1" role="dialog" aria-labelledby="batchProgressModalLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content bg-light-blue border-info">
        <div class="modal-header text-black text-uppercase border-0">
          <h4 class="modal-title">
            <i class="bx bx-loader-alt bx-spin me-2" v-if="isProcessing && !isPaused"></i>
            <i class="bx bx-pause-circle me-2 text-warning" v-if="isPaused"></i>
            <i class="bx bx-check-circle text-success me-2" v-if="isCompleted"></i>
            Bulk Invoice Processing
          </h4>
          <button type="button" class="btn-close" @click="handleClose" :disabled="isProcessing && !isPaused" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!-- Fiscal Year Badge - Always visible at top -->
          <div class="fiscal-year-banner bg-primary text-white p-3 rounded mb-4 d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <i class="bx bx-calendar fs-3 me-3"></i>
              <div>
                <small class="text-white-50 d-block">Invoicing for Fiscal Year</small>
                <h3 class="mb-0 fw-bold">{{ fiscalYear }}</h3>
              </div>
            </div>
            <div class="text-end">
              <small class="text-white-50 d-block">Status</small>
              <span class="badge fs-6" :class="fiscalYearStatusBadgeClass">
                {{ fiscalYearStatusText }}
              </span>
            </div>
          </div>

          <!-- Processing Status -->
          <div v-if="!isCompleted" class="processing-section">
            <!-- Status Alert -->
            <div :class="statusAlertClass" class="alert border-2" role="alert">
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <h5 class="alert-heading mb-1">{{ statusTitle }}</h5>
                  <p class="mb-0">{{ statusMessage }}</p>
                </div>
                <div v-if="isProcessing && !isPaused" class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Processing...</span>
                </div>
              </div>
            </div>

            <!-- Progress Bar -->
            <div class="progress-section my-4">
              <div class="d-flex justify-content-between mb-2">
                <span class="fw-bold">Progress</span>
                <span class="fw-bold">{{ progressPercentage }}%</span>
              </div>
              <div class="progress" style="height: 25px;">
                <div
                    class="progress-bar progress-bar-striped"
                    :class="progressBarClass"
                    role="progressbar"
                    :style="{ width: progressPercentage + '%' }"
                    :aria-valuenow="progressPercentage"
                    aria-valuemin="0"
                    aria-valuemax="100">
                  {{ processedCount }} / {{ totalToProcess }}
                </div>
              </div>
            </div>

            <!-- Statistics Grid -->
            <div class="row text-center mb-4">
              <div class="col-3">
                <div class="card bg-light border-0">
                  <div class="card-body py-3">
                    <h3 class="text-primary mb-0">{{ numberFormat(totalToProcess) }}</h3>
                    <small class="text-muted">Total Records</small>
                  </div>
                </div>
              </div>
              <div class="col-3">
                <div class="card bg-light border-0">
                  <div class="card-body py-3">
                    <h3 class="text-success mb-0">{{ numberFormat(successCount) }}</h3>
                    <small class="text-muted">Successful</small>
                  </div>
                </div>
              </div>
              <div class="col-3">
                <div class="card bg-light border-0">
                  <div class="card-body py-3">
                    <h3 class="text-danger mb-0">{{ numberFormat(errorCount) }}</h3>
                    <small class="text-muted">Errors</small>
                  </div>
                </div>
              </div>
              <div class="col-3">
                <div class="card bg-light border-0">
                  <div class="card-body py-3">
                    <h3 class="text-info mb-0">{{ numberFormat(skippedCount) }}</h3>
                    <small class="text-muted">Skipped</small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Time Estimates -->
            <div class="row text-center mb-4">
              <div class="col-4">
                <div class="d-flex flex-column">
                  <span class="text-muted small">Elapsed Time</span>
                  <span class="fw-bold fs-5">{{ formatTime(elapsedTime) }}</span>
                </div>
              </div>
              <div class="col-4">
                <div class="d-flex flex-column">
                  <span class="text-muted small">Estimated Remaining</span>
                  <span class="fw-bold fs-5">{{ formatTime(estimatedTimeRemaining) }}</span>
                </div>
              </div>
              <div class="col-4">
                <div class="d-flex flex-column">
                  <span class="text-muted small">Processing Speed</span>
                  <span class="fw-bold fs-5">{{ processingSpeed }} rec/min</span>
                </div>
              </div>
            </div>

            <!-- Current Batch Info -->
            <div class="current-batch-info bg-light p-3 rounded mb-4">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <small class="text-muted">Current Batch</small>
                  <div class="fw-bold">Batch {{ currentBatch }} of {{ totalBatches }}</div>
                </div>
                <div>
                  <small class="text-muted">Page</small>
                  <div class="fw-bold">{{ currentPage }} of {{ totalPages }}</div>
                </div>
                <div>
                  <small class="text-muted">Batch Size</small>
                  <div class="fw-bold">{{ batchSize }} records</div>
                </div>
              </div>
            </div>

            <!-- Current Processing Item -->
            <div v-if="currentItem" class="current-item-info bg-info bg-opacity-10 p-3 rounded mb-4 border border-info">
              <small class="text-muted d-block mb-1">Currently Processing:</small>
              <div class="fw-bold">{{ currentItem.businessName }}</div>
              <small class="text-muted">ID: {{ currentItem.businessID }} | Phone: {{ currentItem.ownerPhone }}</small>
            </div>

            <!-- Error Log (collapsible) -->
            <div v-if="errorLog.length > 0" class="error-log-section mb-4">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="text-danger mb-0">
                  <i class="bx bx-error-circle me-1"></i>
                  Error Log ({{ errorLog.length }})
                </h6>
                <button class="btn btn-sm btn-outline-danger" type="button" @click="toggleErrors">
                  {{ showErrors ? 'Hide' : 'Show' }} Errors
                </button>
              </div>
              <div v-if="showErrors" class="card card-body bg-danger bg-opacity-10 border-danger" style="max-height: 150px; overflow-y: auto;">
                <ul class="list-unstyled mb-0 small">
                  <li v-for="(error, index) in errorLog" :key="index" class="mb-1">
                    <strong>{{ error.businessID }} - {{ error.businessName }}:</strong> {{ error.message }}
                  </li>
                </ul>
              </div>
            </div>

            <!-- Control Buttons -->
            <div class="d-flex justify-content-between gap-3">
              <button
                  v-if="!isPaused && isProcessing"
                  @click="onPause"
                  class="btn btn-warning flex-grow-1 text-uppercase fw-bold">
                <i class="bx bx-pause me-2"></i>Pause
              </button>
              <button
                  v-if="isPaused"
                  @click="onResume"
                  class="btn btn-success flex-grow-1 text-uppercase fw-bold">
                <i class="bx bx-play me-2"></i>Resume
              </button>
              <button
                  v-if="isPaused && errorCount > 0"
                  @click="onRetryErrors"
                  class="btn btn-info flex-grow-1 text-uppercase fw-bold">
                <i class="bx bx-refresh me-2"></i>Retry Errors ({{ errorCount }})
              </button>
              <button
                  @click="onCancel"
                  class="btn btn-outline-danger flex-grow-1 text-uppercase fw-bold">
                <i class="bx bx-x me-2"></i>{{ isPaused ? 'Cancel' : 'Stop' }}
              </button>
            </div>
          </div>

          <!-- Completion Section -->
          <div v-if="isCompleted" class="completion-section text-center">
            <div class="mb-4">
              <div class="avatar-lg rounded-circle bg-success mx-auto d-flex align-items-center justify-content-center" style="width: 80px; height: 80px;">
                <i class="bx bx-check text-white" style="font-size: 48px;"></i>
              </div>
            </div>
            <h4 class="text-uppercase text-success fw-bold mb-3">Processing Complete!</h4>

            <!-- Fiscal Year Confirmation -->
            <p class="text-muted mb-4">
              All invoices have been generated for <strong class="text-primary">Fiscal Year {{ fiscalYear }}</strong>
            </p>

            <!-- Final Statistics -->
            <div class="row text-center mb-4">
              <div class="col-4">
                <div class="card bg-success bg-opacity-10 border-success">
                  <div class="card-body py-3">
                    <h2 class="text-success mb-0">{{ numberFormat(successCount) }}</h2>
                    <small class="text-success">Invoices Sent</small>
                  </div>
                </div>
              </div>
              <div class="col-4">
                <div class="card bg-danger bg-opacity-10 border-danger">
                  <div class="card-body py-3">
                    <h2 class="text-danger mb-0">{{ numberFormat(errorCount) }}</h2>
                    <small class="text-danger">Failed</small>
                  </div>
                </div>
              </div>
              <div class="col-4">
                <div class="card bg-info bg-opacity-10 border-info">
                  <div class="card-body py-3">
                    <h2 class="text-info mb-0">{{ numberFormat(skippedCount) }}</h2>
                    <small class="text-info">Skipped</small>
                  </div>
                </div>
              </div>
            </div>

            <p class="text-muted">
              Total processing time: <strong>{{ formatTime(elapsedTime) }}</strong>
            </p>

            <!-- Final Error Log -->
            <div v-if="errorLog.length > 0" class="error-log-section mb-4 text-start">
              <div class="alert alert-danger">
                <h6 class="alert-heading">
                  <i class="bx bx-error-circle me-1"></i>
                  Failed Records ({{ errorLog.length }})
                </h6>
                <div style="max-height: 150px; overflow-y: auto;">
                  <ul class="mb-0 small">
                    <li v-for="(error, index) in errorLog" :key="index">
                      <strong>{{ error.businessID }} - {{ error.businessName }}:</strong> {{ error.message }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Final Actions -->
            <div class="d-flex justify-content-center gap-3">
              <button v-if="errorLog.length > 0" @click="onRetryErrors" class="btn btn-warning text-uppercase fw-bold">
                <i class="bx bx-refresh me-2"></i>Retry Failed ({{ errorLog.length }})
              </button>
              <button @click="handleClose" class="btn btn-primary text-uppercase fw-bold">
                <i class="bx bx-check me-2"></i>Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: "BatchProgressModal",
  props: {
    isProcessing: {
      type: Boolean,
      default: false
    },
    isPaused: {
      type: Boolean,
      default: false
    },
    isCompleted: {
      type: Boolean,
      default: false
    },
    hasErrors: {
      type: Boolean,
      default: false
    },
    totalToProcess: {
      type: Number,
      default: 0
    },
    processedCount: {
      type: Number,
      default: 0
    },
    successCount: {
      type: Number,
      default: 0
    },
    errorCount: {
      type: Number,
      default: 0
    },
    skippedCount: {
      type: Number,
      default: 0
    },
    currentBatch: {
      type: Number,
      default: 0
    },
    totalBatches: {
      type: Number,
      default: 0
    },
    currentPage: {
      type: Number,
      default: 1
    },
    totalPages: {
      type: Number,
      default: 1
    },
    batchSize: {
      type: Number,
      default: 10
    },
    elapsedTime: {
      type: Number,
      default: 0
    },
    estimatedTimeRemaining: {
      type: Number,
      default: 0
    },
    errorLog: {
      type: Array,
      default: () => []
    },
    currentItem: {
      type: Object,
      default: null
    },
    // New prop for fiscal year
    fiscalYear: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      showErrors: false
    }
  },
  computed: {
    progressPercentage() {
      if (this.totalToProcess === 0) return 0;
      return Math.round((this.processedCount / this.totalToProcess) * 100);
    },
    progressBarClass() {
      let classes = [];
      if (this.isProcessing && !this.isPaused) {
        classes.push('progress-bar-animated');
      }
      if (this.isPaused) {
        classes.push('bg-warning');
      } else if (this.isCompleted) {
        classes.push('bg-success');
      } else if (this.hasErrors) {
        classes.push('bg-danger');
      } else {
        classes.push('bg-primary');
      }
      return classes;
    },
    statusAlertClass() {
      if (this.isCompleted) return 'alert-success border-success';
      if (this.hasErrors) return 'alert-danger border-danger';
      if (this.isPaused) return 'alert-warning border-warning';
      return 'alert-info border-info';
    },
    statusTitle() {
      if (this.isCompleted) return 'Processing Complete';
      if (this.hasErrors && this.isPaused) return 'Processing Paused - Errors Detected';
      if (this.isPaused) return 'Processing Paused';
      return 'Processing Invoices...';
    },
    statusMessage() {
      if (this.isCompleted) {
        return `Successfully processed ${this.successCount} invoices for Fiscal Year ${this.fiscalYear}.`;
      }
      if (this.isPaused) {
        return 'Click Resume to continue processing or Cancel to stop.';
      }
      return `Processing batch ${this.currentBatch} of ${this.totalBatches} for FY ${this.fiscalYear}. Please do not close this window.`;
    },
    processingSpeed() {
      if (this.elapsedTime === 0 || this.processedCount === 0) return '0';
      const recordsPerSecond = this.processedCount / this.elapsedTime;
      const recordsPerMinute = recordsPerSecond * 60;
      return recordsPerMinute.toFixed(1);
    },
    // Fiscal year status badge class
    fiscalYearStatusBadgeClass() {
      if (this.isCompleted) return 'bg-success';
      if (this.isPaused) return 'bg-warning text-dark';
      if (this.hasErrors) return 'bg-danger';
      return 'bg-light text-dark';
    },
    // Fiscal year status text
    fiscalYearStatusText() {
      if (this.isCompleted) return 'Completed';
      if (this.isPaused) return 'Paused';
      if (this.hasErrors) return 'Has Errors';
      if (this.isProcessing) return 'Processing';
      return 'Ready';
    }
  },
  methods: {
    numberFormat(num) {
      const britishNumberFormatter = new Intl.NumberFormat("en-GB");
      return britishNumberFormatter.format(num);
    },
    formatTime(seconds) {
      if (seconds === 0 || isNaN(seconds) || !isFinite(seconds)) return '00:00:00';
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = Math.floor(seconds % 60);
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    toggleErrors() {
      this.showErrors = !this.showErrors;
    },
    onPause() {
      this.$emit('pause');
    },
    onResume() {
      this.$emit('resume');
    },
    onCancel() {
      if (this.isProcessing && !this.isPaused) {
        if (confirm('Are you sure you want to stop the batch processing? You can resume from where you left off.')) {
          this.$emit('pause');
        }
      } else {
        if (confirm('Are you sure you want to cancel? Any unprocessed records will need to be processed again.')) {
          this.$emit('cancel');
        }
      }
    },
    onRetryErrors() {
      this.$emit('retryErrors');
    },
    handleClose() {
      if (this.isProcessing && !this.isPaused) {
        alert('Please pause or wait for processing to complete before closing.');
        return;
      }
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
.batch-progress-modal .modal-content {
  border-radius: 12px;
}

.progress {
  border-radius: 12px;
  overflow: hidden;
}

.progress-bar {
  transition: width 0.3s ease;
}

.avatar-lg {
  width: 80px;
  height: 80px;
}

.card {
  transition: transform 0.2s ease;
}

.card:hover {
  transform: translateY(-2px);
}

.bx-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.fiscal-year-banner {
  background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
}
</style>